/* eslint-disable max-lines-per-function */
import { toast } from '@/hooks/use-toast';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  DrawerContent,
  DrawerTrigger,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@e2n/ui';
import { Abschnitt } from '@/domain/mitarbeiter/features/rotationsplan/entities/Abschnitt.ts';
import { Rotationsplan } from '@/domain/mitarbeiter/features/rotationsplan/entities/Rotationsplan.ts';
import { useDeleteAbschnitt } from '@/domain/mitarbeiter/features/rotationsplan/hooks/useDeleteAbschnitt/useDeleteAbschnitt.ts';

export function DeleteAbschnitt({
  abschnitt,
  rotationsplan
}: {
  abschnitt: Abschnitt;
  rotationsplan: Rotationsplan;
}) {
  const { id } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [deleteAbschnitt] = useDeleteAbschnitt();

  const { t } = useTranslation();
  const navigate = useNavigate();
  const istLehrjahr = rotationsplan.beruf.istAusbildung;

  const keineStationenVorhanden = abschnitt.stationen.length === 0;

  async function handleDelete() {
    setIsSubmitting(true);
    const result = await deleteAbschnitt({
      abschnittId: abschnitt.id,
      rotationsplanId: rotationsplan.id,
      position: abschnitt.position,
    });

    if (
      result.error ||
      result.data?.mitarbeiter_deleteAbschnitt.error
    ) {
      toast({
        description: t(
          istLehrjahr
            ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.error.lehrjahr'
            : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.error.abschnitt'
        ),
      });
    } else {
      navigate(`/mitarbeiter/rotationsplan/${id}`);
      toast({
        description: t(
          istLehrjahr
            ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.success.lehrjahr'
            : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.success.abschnitt'
        ),
      });
    }
    setIsSubmitting(false);
  }

  return (
    <>
      {/*Desktop Dialog*/}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="hidden md:max-w-111 p-8 md:flex flex-col gap-6">
          <DialogTitle>
            {t('domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.title', { name: abschnitt.position + '. ' + abschnitt.name, })}
          </DialogTitle>
          <div className="flex flex-col gap-2 text-sm">
            <div className="text-neutrals-900">
              {t(
                istLehrjahr
                  ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.lehrjahr'
                  : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.abschnitt'
              )}
            </div>
            <div className="text-primary-red-600">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.infoStandortwechsel',
              )}
            </div>
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button
              onClick={() => {
                setIsDialogOpen(false);
              }}
              variant="outline">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.dismiss',
              )}
            </Button>
            <Button
              disabled={isSubmitting}
              variant="destructive"
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.confirm',
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild className="hidden md:flex">
            <Button
              disabled={isSubmitting}
              onClick={() => {
                if (keineStationenVorhanden) {
                  handleDelete()
                  } else {
                  setIsDialogOpen(true)
                }
              }}
              variant="ghost-destructive"
              className="hover:bg-neutrals-300 md:hover:bg-neutrals-400"
              size="icon">
              <FontAwesomeIcon icon={faTrashCan} />
            </Button>
          </TooltipTrigger>
          <TooltipContent side={'bottom'}>
            {t('domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.tooltip')}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/*Mobile Drawer*/}
      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen}>
        <DrawerTrigger className="flex md:hidden" asChild>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild className="flex md:hidden">
                <Button
                  disabled={isSubmitting}
                  onClick={() => {
                    if (keineStationenVorhanden) {
                      handleDelete()
                    } else {
                      setIsDrawerOpen(true)
                    }
                  }}
                  variant="ghost-destructive"
                  className="hover:bg-neutrals-300 md:hover:bg-neutrals-400"
                  size="icon">
                  <FontAwesomeIcon icon={faTrashCan} />
                </Button>
              </TooltipTrigger>
              <TooltipContent side={'bottom'}>
                {t('domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.tooltip')}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DrawerTrigger>
        <DrawerContent className="flex flex-col items-center gap-5 p-4 md:hidden">
          <div className="flex flex-col items-center text-center gap-2 text-sm">
            <div className="font-semibold">
              {t('domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.title', { name: abschnitt.position + '. ' + abschnitt.name, })}
            </div>
            <div>
              {t(
                istLehrjahr
                  ? 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.lehrjahr'
                  : 'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.subtitle.abschnitt'
              )}
            </div>
            <div className="text-primary-red-600">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.infoStandortwechsel',
              )}
            </div>
          </div>
          <div className="flex flex-col gap-3 items-center w-full">
            <Button
              className="w-full"
              onClick={() => {
                setIsDrawerOpen(false);
              }}
              variant="outline">
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.dismiss',
              )}
            </Button>
            <Button
              className="w-full"
              variant="destructive"
              disabled={isSubmitting}
              onClick={handleDelete}>
              {t(
                'domain.mitarbeiter.rotationsplan.rotationsplaene.deleteAbschnitt.confirm',
              )}
            </Button>
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}
